import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { commonButtonProps } from "../../../utils/styles";
import { commonTextFieldProps } from "../../../utils/styles";

interface Mechanic {
  id?: string;
  name: string;
  phoneNumber: string;
  location?: string;
  companyId?: string;
  idType?: string;
  idNumber?: string;
}

interface AddMechanicModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (mechanic: Omit<Mechanic, "id">) => void;
  mechanic?: Mechanic;
  isEdit?: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Mechanic name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters"),
  phoneNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9]+$/, "Contact number must contain only digits")
    .min(10, "Contact number must be exactly 10 digits")
    .max(10, "Contact number must be exactly 10 digits"),
  location: Yup.string().max(255, "Location must not exceed 100 characters"),
});

const initialValues: Omit<Mechanic, "id"> = {
  name: "",
  phoneNumber: "",
  location: "",
  companyId: "",
};

const AddMechanicModal: React.FC<AddMechanicModalProps> = ({
  open,
  onClose,
  onSubmit,
  mechanic,
  isEdit = false,
}) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;

  const handleSubmit = (values: Omit<Mechanic, "id">) => {
    const enrichedValues = {
      ...values,
      ...(!isEdit && { companyId: companyId }),
    };
    onSubmit(enrichedValues);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Update Mechanic" : "Add New Mechanic"}
      </DialogTitle>
      <Formik
        initialValues={mechanic || initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form>
            <DialogContent sx={{ p: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Mechanic Name"
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Contact Number"
                    name="phoneNumber"
                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    inputProps={{
                      maxLength: 10,
                    }}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="ID Proof"
                    name="idType"
                    error={touched.idType && Boolean(errors.idType)}
                    helperText={touched.idType && errors.idType}
                    placeholder="Aadhaar, PAN, etc."
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="ID Proof Number"
                    name="idNumber"
                    error={touched.idNumber && Boolean(errors.idNumber)}
                    helperText={touched.idNumber && errors.idNumber}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Address"
                    name="location"
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location && errors.location}
                    placeholder="Optional"
                    multiline
                    rows={2}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} {...commonButtonProps}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!(isValid && dirty)}
                {...commonButtonProps}
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddMechanicModal;
